/**
 * SupportPage — one door for bugs, feedback, and general thoughts.
 *
 * Mirrors the mobile SupportScreen: three top-level buckets (Bug / Feedback /
 * General) on the entry screen, each leading to a focused form. Every
 * submission POSTs to /support/tickets with `category` set to literally
 * 'bug' | 'feedback' | 'general' — the exact filters the admin dashboard uses.
 *
 * Replaces the legacy FeedbackModal + BugReportModal pair (which scattered
 * submissions across /feedback vs /support/tickets). With this + the backend
 * admin aggregation, every message ends up in one place regardless of platform.
 */

import { useCallback, useState } from "react";
import PageHeader from "@/components/PageHeader";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { API } from "@/App";
import PageLoader from "@/components/PageLoader";
import { useStaleData } from "@/hooks/useStaleData";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Bug, MessageSquarePlus, MessageCircle, ChevronLeft, ChevronRight,
  Send, Mail, CheckCircle2, Clock, LifeBuoy,
} from "lucide-react";

// Which screen of the app a bug was found in — drives Bug flow's "Where?" step
const BUG_SECTIONS = [
  { key: "onboarding",   label: "Onboarding",        emoji: "🚀" },
  { key: "dashboard",    label: "Dashboard",         emoji: "🏠" },
  { key: "chanakya",     label: "Chanakya (AI chat)",emoji: "💬" },
  { key: "emis",         label: "EMIs",              emoji: "💳" },
  { key: "goals",        label: "Goals",             emoji: "🎯" },
  { key: "investments",  label: "Investments",       emoji: "📈" },
  { key: "income",       label: "Income",            emoji: "💰" },
  { key: "expenses",     label: "Expenses",          emoji: "🛒" },
  { key: "lifetime",     label: "Lifetime Earnings", emoji: "🏆" },
  { key: "calculators",  label: "Calculators (FD/SIP/Tax)", emoji: "🧮" },
  { key: "circle",       label: "Family Circle",     emoji: "👨‍👩‍👧" },
  { key: "profile",      label: "Profile / Settings",emoji: "⚙️" },
  { key: "login",        label: "Login / Signup",    emoji: "🔑" },
  { key: "other",        label: "Something else",    emoji: "❓" },
];

const BUCKETS = {
  bug: {
    emoji: "🐛",
    title: "Report a Bug",
    sub: "Something's broken. Tell us which screen and what happened — we'll fix it fast.",
    cardBg: "bg-red-50 hover:bg-red-100 border-red-200",
    iconBg: "bg-red-100",
    iconColor: "text-red-600",
    titleColor: "text-red-700",
    subColor: "text-red-900/70",
    button: "bg-red-500 hover:bg-red-600",
  },
  feedback: {
    emoji: "💬",
    title: "Feedback",
    sub: "Love something? Want something changed? This shapes what we build next.",
    cardBg: "bg-orange-50 hover:bg-orange-100 border-orange-200",
    iconBg: "bg-orange-100",
    iconColor: "text-orange-600",
    titleColor: "text-orange-700",
    subColor: "text-orange-900/70",
    button: "bg-orange-500 hover:bg-orange-600",
  },
  general: {
    emoji: "💭",
    title: "General",
    sub: "A question, a thought, anything else on your mind — we read every message.",
    cardBg: "bg-blue-50 hover:bg-blue-100 border-blue-200",
    iconBg: "bg-blue-100",
    iconColor: "text-blue-600",
    titleColor: "text-blue-700",
    subColor: "text-blue-900/70",
    button: "bg-blue-500 hover:bg-blue-600",
  },
};

const fmtWhen = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const diffH = Math.floor((now.getTime() - d.getTime()) / 3600000);
  if (diffH < 1) return "Just now";
  if (diffH < 24) return `${diffH}h ago`;
  const diffD = Math.floor(diffH / 24);
  if (diffD < 7) return `${diffD}d ago`;
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
};

export default function SupportPage() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  // 'menu' | 'bug' | 'feedback' | 'general'
  const [mode, setMode] = useState("menu");

  // Bug flow — Step 1/2/3 fields match the admin dashboard schema.
  const [bugSection,  setBugSection]  = useState("");   // Step 1 — where
  const [bugSubject,  setBugSubject]  = useState("");   // Step 2 — one-line summary
  const [bugExpected, setBugExpected] = useState("");   // Step 3a — should have happened
  const [bugActual,   setBugActual]   = useState("");   // Step 3b — actually happened
  const [bugSteps,    setBugSteps]    = useState("");   // Step 3c — reproduction

  // Feedback / General flow (shared form — differs only in category sent)
  const [fbFeature, setFbFeature] = useState("");
  const [fbDetails, setFbDetails] = useState("");

  const [submitting, setSubmitting] = useState(false);

  const fetchTickets = useCallback(async () => {
    const res = await axios.get(`${API}/support/tickets`, { headers: { Authorization: `Bearer ${token}` } });
    return res.data?.tickets || [];
  }, [token]);

  const { data, loading, reload } = useStaleData(
    "bm_support_tickets_cache",
    fetchTickets,
    { errorMsg: "Failed to load your tickets", fallback: [] }
  );
  const tickets = data || [];

  const resetForms = () => {
    setBugSection(""); setBugSubject("");
    setBugExpected(""); setBugActual(""); setBugSteps("");
    setFbFeature(""); setFbDetails("");
  };

  const goBack = () => { setMode("menu"); resetForms(); };

  const submitBug = async () => {
    if (!bugSection)                          { toast.error("Pick a section — which screen is the bug in?"); return; }
    if (bugSubject.trim().length < 3)         { toast.error("One-line summary needs at least 3 characters."); return; }
    // Step 3 needs at least an Actual OR Steps to be useful — Expected is nice-to-have
    const step3Total = (bugExpected.trim() + bugActual.trim() + bugSteps.trim()).length;
    if (step3Total < 10)                      { toast.error("Fill in what actually happened (and/or steps). 10+ chars."); return; }
    setSubmitting(true);
    try {
      const sectionMeta = BUG_SECTIONS.find((b) => b.key === bugSection);
      const sectionLabel = sectionMeta?.label || bugSection;
      await axios.post(`${API}/support/tickets`, {
        category: "bug",
        subject: `[${sectionLabel}] ${bugSubject.trim()}`,
        // Structured Step 1/2/3 fields — admin dashboard renders these.
        // Backend also synthesises a legacy `description` from them so
        // older admin UIs stay readable.
        section:      sectionLabel,
        bug_expected: bugExpected.trim(),
        bug_actual:   bugActual.trim(),
        bug_steps:    bugSteps.trim(),
        platform:     "web",
        app_version:  "",
      }, { headers });
      toast.success("Bug reported — we'll look into it");
      resetForms();
      setMode("menu");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send — please try again.");
    } finally { setSubmitting(false); }
  };

  const submitFeedback = async (category) => {
    if (!fbFeature.trim())             { toast.error("Tell us which feature or screen this is about."); return; }
    if (fbDetails.trim().length < 10)  { toast.error("Just a sentence or two (10+ chars) — we want to get it right."); return; }
    setSubmitting(true);
    try {
      const bucket = BUCKETS[category];
      await axios.post(`${API}/support/tickets`, {
        category, // 'feedback' | 'general' — matches admin dashboard buckets exactly
        subject: `${bucket.emoji} [${fbFeature.trim()}] ${fbDetails.trim().slice(0, 60)}`,
        description: `Kind: ${bucket.title}\nFeature: ${fbFeature.trim()}\n\n${fbDetails.trim()}`,
        platform: "web",
        app_version: "",
      }, { headers });
      toast.success("Got it — we're listening");
      resetForms();
      setMode("menu");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send — please try again.");
    } finally { setSubmitting(false); }
  };

  if (loading) {
    return (
      <>
        <div className="min-h-[calc(100vh-80px)] flex items-center justify-center"
          style={{ background: "linear-gradient(160deg,#fafaf9 0%,#fff7ed 100%)" }}>
          <PageLoader message="Loading Help & Support…" tips={[
            "We read every message we get 📬",
            "Three buckets: Bug / Feedback / General",
            "Replies within 24 hours from mantrabudget@gmail.com",
          ]} />
        </div>
      </>
    );
  }

  const heading =
    mode === "bug"      ? "Report a Bug"   :
    mode === "feedback" ? "Share Feedback" :
    mode === "general"  ? "General"        :
    "Help & Support";

  return (
    <div className="min-h-screen" style={{ background: "linear-gradient(160deg,#fafaf9 0%,#fff7ed 100%)" }}>

      <PageHeader
        icon={<LifeBuoy size={20} />}
        title="Help & Support"
        subtitle="Get help, report bugs, share feedback"
        color="orange"
      >
        {mode !== "menu" && (
          <button onClick={goBack}
            className="flex items-center gap-1.5 text-sm font-medium text-stone-500 hover:text-stone-700 transition-colors">
            <ChevronLeft size={16} /> Back
          </button>
        )}
      </PageHeader>

      <div className="max-w-3xl mx-auto px-4 py-6 pb-28 lg:pb-10 space-y-4">

        {/* ── Menu ────────────────────────────────────────────────────────── */}
        {mode === "menu" && (
          <>
            {/* Three bucket cards */}
            {(["bug", "feedback", "general"]).map((k) => {
              const b = BUCKETS[k];
              const Icon = k === "bug" ? Bug : k === "feedback" ? MessageSquarePlus : MessageCircle;
              return (
                <button
                  key={k}
                  onClick={() => setMode(k)}
                  className={`w-full flex items-center gap-4 p-5 rounded-2xl border-2 text-left transition-colors ${b.cardBg}`}
                >
                  <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 ${b.iconBg}`}>
                    <Icon size={22} className={b.iconColor} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-base font-extrabold font-['Outfit'] mb-0.5 ${b.titleColor}`}>{b.title}</p>
                    <p className={`text-xs sm:text-sm leading-snug ${b.subColor}`}>{b.sub}</p>
                  </div>
                  <ChevronRight size={18} className={b.iconColor} />
                </button>
              );
            })}

            {/* Past tickets */}
            {tickets.length > 0 && (
              <div className="pt-3">
                <p className="text-[11px] font-bold text-stone-500 uppercase tracking-widest mb-2 px-1">
                  Your past conversations
                </p>
                <div className="bg-white rounded-2xl border border-stone-200 shadow-sm overflow-hidden divide-y divide-stone-50">
                  {tickets.map((t) => {
                    const b = BUCKETS[t.category] || BUCKETS.general;
                    return (
                      <div key={t.id} className="flex gap-3 p-4">
                        <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${b.iconBg}`}>
                          <span className="text-base">{b.emoji}</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-bold text-stone-800 truncate">{t.subject}</p>
                          <p className="text-xs text-stone-500 mt-0.5 line-clamp-2 whitespace-pre-wrap">{t.description}</p>
                          <div className="flex items-center gap-2 mt-2">
                            <span className="text-[11px] text-stone-400 font-medium">{fmtWhen(t.created_at)}</span>
                            <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full ${
                              t.status === "open"
                                ? "bg-amber-100 text-amber-700 border border-amber-200"
                                : "bg-emerald-100 text-emerald-700 border border-emerald-200"
                            }`}>
                              {t.status === "open" ? <Clock size={9} /> : <CheckCircle2 size={9} />}
                              {t.status === "open" ? "Awaiting reply" : "Resolved"}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Email footer */}
            <div className="mt-4 bg-orange-50 border border-orange-200 rounded-2xl p-4 flex items-center gap-3">
              <Mail size={16} className="text-orange-500 shrink-0" />
              <p className="text-sm text-stone-700">
                Prefer email? Write to <a href="mailto:mantrabudget@gmail.com"
                  className="font-bold text-orange-600 hover:underline">mantrabudget@gmail.com</a>
              </p>
            </div>
          </>
        )}

        {/* ── Bug flow ──────────────────────────────────────────────────── */}
        {mode === "bug" && (
          <div className="bg-white rounded-2xl border border-stone-200 shadow-sm p-5 space-y-5">
            <div>
              <p className="text-[11px] font-bold text-red-600 uppercase tracking-widest mb-1">Step 1 of 3</p>
              <p className="text-lg font-extrabold text-stone-800 font-['Outfit'] mb-1">Where did it happen?</p>
              <p className="text-xs text-stone-500 mb-3">Pick the screen or feature so we know where to look.</p>
              <div className="flex flex-wrap gap-2">
                {BUG_SECTIONS.map((sec) => {
                  const selected = bugSection === sec.key;
                  return (
                    <button
                      key={sec.key}
                      onClick={() => setBugSection(sec.key)}
                      className={`flex items-center gap-1.5 px-3 py-2 rounded-full border text-xs font-semibold transition-colors ${
                        selected
                          ? "bg-red-50 border-red-300 text-red-700"
                          : "bg-stone-50 border-stone-200 text-stone-600 hover:bg-red-50 hover:border-red-200"
                      }`}
                    >
                      <span>{sec.emoji}</span>
                      <span>{sec.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {!!bugSection && (
              <>
                <div>
                  <p className="text-[11px] font-bold text-red-600 uppercase tracking-widest mb-1">Step 2 of 3</p>
                  <Label className="text-sm font-medium text-stone-700">What went wrong? (one line)</Label>
                  <Input
                    value={bugSubject}
                    onChange={(e) => setBugSubject(e.target.value)}
                    placeholder="e.g. Dashboard shows ₹0 after adding salary"
                    maxLength={140}
                    className="mt-1"
                  />
                </div>

                <div>
                  <p className="text-[11px] font-bold text-red-600 uppercase tracking-widest mb-1">Step 3 of 3</p>
                  <Label className="text-sm font-medium text-stone-700">Details</Label>
                  <div className="space-y-3 mt-1">
                    <div>
                      <p className="text-[11px] font-semibold text-stone-500 mb-1">Expected — what should have happened?</p>
                      <textarea
                        rows={2}
                        value={bugExpected}
                        onChange={(e) => setBugExpected(e.target.value)}
                        maxLength={2000}
                        placeholder="e.g. Transaction should be saved and show in Recent"
                        className="w-full border border-stone-200 rounded-xl px-3 py-2.5 text-sm text-stone-700 placeholder:text-stone-300 focus:outline-none focus:border-orange-400 resize-none"
                      />
                    </div>
                    <div>
                      <p className="text-[11px] font-semibold text-stone-500 mb-1">Actual — what actually happened?</p>
                      <textarea
                        rows={2}
                        value={bugActual}
                        onChange={(e) => setBugActual(e.target.value)}
                        maxLength={2000}
                        placeholder="e.g. Chanakya replied 'logged!' but nothing appeared in Expenses"
                        className="w-full border border-stone-200 rounded-xl px-3 py-2.5 text-sm text-stone-700 placeholder:text-stone-300 focus:outline-none focus:border-orange-400 resize-none"
                      />
                    </div>
                    <div>
                      <p className="text-[11px] font-semibold text-stone-500 mb-1">Steps — how do we reproduce it?</p>
                      <textarea
                        rows={3}
                        value={bugSteps}
                        onChange={(e) => setBugSteps(e.target.value)}
                        maxLength={2000}
                        placeholder='e.g. 1. Open Chanakya chat\n2. Type "Spent 60 for tea"\n3. Check Expenses page'
                        className="w-full border border-stone-200 rounded-xl px-3 py-2.5 text-sm text-stone-700 placeholder:text-stone-300 focus:outline-none focus:border-orange-400 resize-none"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex gap-2 pt-1">
                  <Button type="button" variant="outline" onClick={goBack} className="flex-1">Cancel</Button>
                  <Button
                    onClick={submitBug}
                    disabled={submitting}
                    className={`flex-[2] text-white font-bold ${BUCKETS.bug.button}`}
                  >
                    <Send size={14} className="mr-1.5" />
                    {submitting ? "Sending…" : "Send bug report"}
                  </Button>
                </div>
              </>
            )}
          </div>
        )}

        {/* ── Feedback / General flow ─────────────────────────────────── */}
        {(mode === "feedback" || mode === "general") && (
          <div className="bg-white rounded-2xl border border-stone-200 shadow-sm p-5 space-y-5">
            <div>
              <p className={`text-[11px] font-bold uppercase tracking-widest mb-1 ${
                mode === "feedback" ? "text-orange-600" : "text-blue-600"
              }`}>Step 1 of 2</p>
              <Label className="text-sm font-medium text-stone-700">Which feature or screen?</Label>
              <Input
                value={fbFeature}
                onChange={(e) => setFbFeature(e.target.value)}
                placeholder="e.g. Chanakya chat, the onboarding flow"
                className="mt-1"
              />
            </div>

            <div>
              <p className={`text-[11px] font-bold uppercase tracking-widest mb-1 ${
                mode === "feedback" ? "text-orange-600" : "text-blue-600"
              }`}>Step 2 of 2</p>
              <Label className="text-sm font-medium text-stone-700">
                {mode === "feedback" ? "Tell us what you think" : "Anything on your mind?"}
              </Label>
              <textarea
                rows={6}
                value={fbDetails}
                onChange={(e) => setFbDetails(e.target.value)}
                maxLength={4000}
                placeholder={mode === "feedback"
                  ? "What's working, what's not, and anything you'd change."
                  : "General thoughts, ideas, questions — anything."}
                className="mt-1 w-full border border-stone-200 rounded-xl px-3 py-2.5 text-sm text-stone-700 placeholder:text-stone-300 focus:outline-none focus:border-orange-400 resize-none"
              />
            </div>

            <div className="flex gap-2 pt-1">
              <Button type="button" variant="outline" onClick={goBack} className="flex-1">Cancel</Button>
              <Button
                onClick={() => submitFeedback(mode)}
                disabled={submitting}
                className={`flex-[2] text-white font-bold ${BUCKETS[mode].button}`}
              >
                <Send size={14} className="mr-1.5" />
                {submitting ? "Sending…" : "Send"}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
